import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Card,
  Modal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TextInput,
} from 'corso-admin-ui';
import { CountryCode, CrewMerchantUi } from 'corso-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '~/api';
import Address from '~/components/Address';
import ClipboardButton from '~/components/ClipboardButton';
import { StoreCustomerClaimOverview } from '~/components/CustomerClaimOverview';
import OrderReference from '~/components/OrderReference';
import { TagsCard } from '~/components/TagsCard';
import VerifyAddressButton from '~/components/VerifyAddressButton';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { RegistrationSummary } from '~/features/claimReview/components/RegistrationSummary';
import { ClaimLedgerCard } from '~/features/claimReview/components/summary/ClaimLedgerSummary';
import {
  useClaimTags,
  useClaimTagsUpsert,
  useStoreClaimTags,
} from '~/hooks/useClaimTags';
import { useStoreId } from '~/providers/StoreUserProvider';
import ClaimRiskAssessment from './ClaimRiskAssessment';
import ClaimShipmentsDisplay from './shipments/ClaimShipment';

type ClaimUpdateCustomerInfo = CrewMerchantUi.ClaimUpdateCustomerInfo;
const claimUpdateCustomerInfo = CrewMerchantUi.claimUpdateCustomerInfoSchema;

function EditCustomerInfo({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();

  const claim = useClaim();

  const { mutateAsync: updateCustomerInfo, isPending } = useMutation({
    mutationFn: (customerInfoUpdate: ClaimUpdateCustomerInfo) =>
      api
        .store(storeId)
        .claim(`${claim.id}`)
        .updateCustomerInfo(customerInfoUpdate),
    onSuccess: () => claim.refetch(),
  });

  const formId = 'edit-claim-form';
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(claimUpdateCustomerInfo),
    defaultValues: {
      id: claim.id,
      shippingAddress: claim.shippingAddress,
      customerEmail: claim.customerEmail,
    } satisfies ClaimUpdateCustomerInfo,
  });

  const closeAndReset = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      title="Edit Customer"
      key={claim.id}
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(
          async ({ id, customerEmail, shippingAddress }) => {
            await updateCustomerInfo({
              id,
              customerEmail,
              shippingAddress,
            });
            onClose();
          },
        )}
        className="flex flex-col gap-4"
      >
        <TextInput
          id="first-name"
          label="First Name"
          autoComplete="first-name"
          required
          {...register('shippingAddress.firstName')}
          error={errors.shippingAddress?.firstName?.message}
        />

        <TextInput
          id="last-name"
          label="Last Name"
          autoComplete="last-name"
          required
          {...register('shippingAddress.lastName')}
          error={errors.shippingAddress?.lastName?.message}
        />

        <TextInput
          id="email"
          label="Email"
          autoComplete="email"
          required
          {...register('customerEmail')}
          error={errors.customerEmail?.message}
        />

        <TextInput
          id="phone"
          label="Phone"
          autoComplete="tel"
          {...register('shippingAddress.phone')}
          error={errors.shippingAddress?.phone?.message}
        />

        <TextInput
          id="shipping-address-line1"
          label="Street Address"
          autoComplete="address-line1"
          required
          {...register('shippingAddress.line1')}
          error={errors.shippingAddress?.line1?.message}
        />

        <TextInput
          id="shipping-address-line2"
          label="Street Address Line 2"
          autoComplete="address-line2"
          {...register('shippingAddress.line2')}
          error={errors.shippingAddress?.line2?.message}
        />

        <TextInput
          id="shipping-address-city"
          label="City"
          autoComplete="address-level2"
          required
          {...register('shippingAddress.city')}
          error={errors.shippingAddress?.city?.message}
        />

        <TextInput
          id="shipping-address-state-or-province"
          label="State or Province Code"
          autoComplete="address-level1"
          required
          {...register('shippingAddress.stateOrProvinceCode')}
          error={errors.shippingAddress?.stateOrProvinceCode?.message}
        />

        <TextInput
          id="shipping-address-postal-code"
          label="Postal Code"
          autoComplete="postal-code"
          required={watch('shippingAddress.countryCode') === CountryCode.US}
          {...register('shippingAddress.postalCode')}
          error={errors.shippingAddress?.postalCode?.message}
        />

        <TextInput
          id="shipping-address-country"
          label="Country Code"
          autoComplete="country"
          required
          {...register('shippingAddress.countryCode')}
          error={errors.shippingAddress?.countryCode?.message}
        />

        <VerifyAddressButton
          address={{
            ...watch('shippingAddress'),
            name: `${watch('shippingAddress.firstName')} ${watch('shippingAddress.lastName')}`.trim(),
          }}
          onApplyCorrections={(corrected) => {
            if (corrected.line1)
              setValue('shippingAddress.line1', corrected.line1);
            if (corrected.line2)
              setValue('shippingAddress.line2', corrected.line2);
            if (corrected.city)
              setValue('shippingAddress.city', corrected.city);
            if (corrected.postalCode)
              setValue('shippingAddress.postalCode', corrected.postalCode);
            if (corrected.stateOrProvinceCode)
              setValue(
                'shippingAddress.stateOrProvinceCode',
                corrected.stateOrProvinceCode,
              );
            if (corrected.countryCode)
              setValue('shippingAddress.countryCode', corrected.countryCode);
          }}
        />
      </form>
    </Modal>
  );
}

/** @deprecated We just need some kind of `Text` component with variants around roles, size, and more. */
function SmallGrayText({ text }: { text: string }) {
  return <p className="text-xs text-neutral-500">{text}</p>;
}

function GiftReturnDisplay() {
  const claim = useClaim();

  const { originalStoreOrder, isGiftReturn } = claim;

  if (!isGiftReturn || !originalStoreOrder.customerEmail) return null;

  const { shippingAddress } = originalStoreOrder;

  const customerName =
    shippingAddress && shippingAddress.firstName && shippingAddress.lastName
      ? `${shippingAddress.firstName} ${shippingAddress.lastName}`
      : 'No Name';

  return (
    <Card.Line>
      <Card.Heading>Gift return</Card.Heading>
      <Popover>
        <PopoverTrigger asChild>
          <Action
            icon="gift"
            accessibilityLabel="Original Customer"
            variant="ghost"
          />
        </PopoverTrigger>
        <PopoverContent align="end">
          <div className="flex flex-col gap-2">
            <Card.Heading>Original customer</Card.Heading>

            <SmallGrayText text={customerName} />

            <div className="flex flex-col">
              <Card.Heading>Contact information</Card.Heading>
              <div className="flex items-center justify-between">
                <SmallGrayText text={originalStoreOrder.customerEmail} />
                <ClipboardButton
                  onClick={() => claim.customerEmail}
                  variant="ghost"
                />
              </div>
            </div>

            {shippingAddress && (
              <div className="flex flex-col gap-2.5">
                <Card.Heading>Shipping address</Card.Heading>
                <div className="flex flex-col gap-1 text-xs text-neutral-500">
                  <p>{shippingAddress.line1}</p>
                  {shippingAddress.line2 && <p>{shippingAddress.line2}</p>}
                  <p>
                    {shippingAddress.city} {shippingAddress.stateOrProvinceCode}{' '}
                    {shippingAddress.postalCode}
                  </p>
                  <p>{shippingAddress.countryCode}</p>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </Card.Line>
  );
}

function CustomerDisplay() {
  const claim = useClaim();

  const { shippingAddress } = claim;
  const { firstName, lastName, phone } = shippingAddress;

  const customerName = `${firstName} ${lastName}`;

  const [showEditClaim, setShowEditClaim] = useState(false);
  const closeEditClaim = () => setShowEditClaim(false);

  return (
    <Card>
      <div className="flex flex-col">
        {/* Title and Edit Button */}
        <div className="flex items-center justify-between">
          <Card.Heading>Customer</Card.Heading>
          <Action
            icon="edit"
            variant="ghost"
            accessibilityLabel="Edit Customer"
            onClick={() => setShowEditClaim(true)}
          />
        </div>
        <SmallGrayText text={customerName} />

        {claim.customerId && (
          <div className="flex flex-col gap-1">
            <StoreCustomerClaimOverview
              claimId={claim.id}
              claimEmail={claim.customerEmail}
              customerName={claim.customerName}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col">
        <Card.Heading>Contact information</Card.Heading>
        <div className="flex items-center justify-between">
          <SmallGrayText text={claim.customerEmail} />
          <ClipboardButton
            onClick={() => claim.customerEmail}
            variant="ghost"
          />
        </div>
        <SmallGrayText text={phone ?? 'No phone'} />
      </div>

      <div className="flex flex-col gap-2.5">
        <Card.Heading>Shipping address</Card.Heading>
        <Address address={shippingAddress} />
      </div>

      {claim.noteToCustomer && (
        <div className="flex flex-col gap-1">
          <Card.Heading>Note to customer</Card.Heading>
          <SmallGrayText text={claim.noteToCustomer} />
        </div>
      )}

      {/* edit claim */}
      <EditCustomerInfo show={showEditClaim} onClose={closeEditClaim} />
    </Card>
  );
}

function ClaimTagDisplay() {
  const { data: storeClaimTags = [], isPending: isOptionsPending } =
    useStoreClaimTags();
  const { data: currentClaimTags = [], isPending: isCurrentPending } =
    useClaimTags();
  const { mutateAsync: upsertTags, isPending: isUpsertPending } =
    useClaimTagsUpsert();

  return (
    <TagsCard
      storeTags={storeClaimTags}
      currentTags={currentClaimTags}
      isLoading={isOptionsPending || isCurrentPending}
      isSaving={isUpsertPending}
      onChange={(tagNames) => {
        upsertTags(tagNames).catch((error) => console.error(error));
      }}
    />
  );
}

function ClaimOrderDisplay() {
  const {
    isGiftReturn,
    originalStoreOrder: { registration, platformAdminUrl, createdOn, orderNo },
  } = useClaim();

  if (registration)
    return <RegistrationSummary registrationId={registration.id} />;

  if (!orderNo || !platformAdminUrl) return null;

  return (
    <Card>
      <OrderReference
        orderNo={orderNo}
        platformAdminUrl={platformAdminUrl}
        createdOn={createdOn}
      />
      {isGiftReturn && <GiftReturnDisplay />}
    </Card>
  );
}

export default function ClaimDetail() {
  const claim = useClaim();

  return (
    <div className="flex flex-col gap-4" key={claim.id}>
      <ClaimOrderDisplay />
      <ClaimLedgerCard />
      <ClaimRiskAssessment />

      <CustomerDisplay />

      <ClaimTagDisplay />
      <ClaimShipmentsDisplay />
    </div>
  );
}
